/**
 * What the validator knows about walking the map (03 §5.2). Built on the sim's own nav
 * grid (`buildStatics`), with three refinements:
 *
 *  - the water barrier, which the sim's grid does not know yet, blocks except at its crossings;
 *  - wire and mined ground are "soft": impassable for the connectivity test, but counted,
 *    so a sealed ring can be shown to be both closed and breachable in one place;
 *  - connected components of the grid as the soldiers see it, for patrol routes.
 */

import type { NavGrid } from '../../sim/astar';
import { type Collider, collidersFor } from '../../sim/props';
import { buildStatics, inPond } from '../../sim/statics';
import { HAZARDS, RANGER } from '../../sim/tuning';
import type { MissionDef, WaterBarrierDef } from '../../sim/types';

export const UNREACHABLE = 0xffff;

export interface NavProbe {
  width: number;
  height: number;
  /** Everything that stops a soldier: props (wire included), ponds, the water barrier. */
  full: NavGrid;
  /** As `full`, without wire: what stays shut whatever the ranger carries. */
  hard: Uint8Array;
  /** Wire-only cells and mined cells. */
  soft: Uint8Array;
  /** Fewest soft cells crossed from the pickup to each cell; UNREACHABLE when walled off. */
  cost: Uint16Array;
  /** Component label per cell of `full` (4-connected); -1 on blocked cells. */
  component: Int32Array;
  /**
   * Cells the sim gives a path cost: depressions, and (hazards/terrain.ts) mined ground and a
   * mini-bunker's field of fire. None of it is ground to drop a pod on.
   */
  costly: Uint8Array;
  /** The sim's colliders for the untouched map: what blocks a line of sight. */
  colliders: Collider[];
}

function distanceToCourse(course: WaterBarrierDef['course'], x: number, z: number): number {
  let best = Infinity;
  const pts = course.points;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (!a || !b) continue;
    const vx = b[0] - a[0];
    const vz = b[1] - a[1];
    const len2 = vx * vx + vz * vz;
    const t = len2 === 0 ? 0 : Math.min(1, Math.max(0, ((x - a[0]) * vx + (z - a[1]) * vz) / len2));
    const dx = x - (a[0] + vx * t);
    const dz = z - (a[1] + vz * t);
    best = Math.min(best, dx * dx + dz * dz);
  }
  return Math.sqrt(best);
}

/** Is this point in a water barrier, away from every crossing? */
export function inBarrier(def: MissionDef, x: number, z: number): boolean {
  for (const b of def.waterBarriers) {
    if (distanceToCourse(b.course, x, z) > b.course.width / 2) continue;
    if (!b.crossings.some((c) => Math.abs(c - x) <= HAZARDS.crossingHalfWidth)) return true;
  }
  return false;
}

/** The sim's own rule for "this collider blocks that nav cell" (statics.ts). */
export function covers(c: Collider, px: number, pz: number, pad: number): boolean {
  if (c.shape === 'circle') return (px - c.x) * (px - c.x) + (pz - c.z) * (pz - c.z) <= (c.r + pad) * (c.r + pad);
  return Math.abs(px - c.x) <= c.hx + pad && Math.abs(pz - c.z) <= c.hz + pad;
}

function labelComponents(grid: NavGrid): Int32Array {
  const { width, height, blocked } = grid;
  const label = new Int32Array(width * height).fill(-1);
  const stack: number[] = [];
  let next = 0;
  for (let start = 0; start < label.length; start++) {
    if (blocked[start] !== 0 || label[start] !== -1) continue;
    label[start] = next;
    stack.push(start);
    while (stack.length > 0) {
      const cell = stack.pop() ?? 0;
      const x = cell % width;
      const around = [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width];
      for (const n of around) {
        if (n < 0 || n >= label.length || blocked[n] !== 0 || label[n] !== -1) continue;
        label[n] = next;
        stack.push(n);
      }
    }
    next++;
  }
  return label;
}

/** Breadth-first by levels: level k holds the cells whose cheapest route from the source crosses k soft cells. */
function softCost(width: number, height: number, hard: Uint8Array, soft: Uint8Array, source: number): Uint16Array {
  const cost = new Uint16Array(width * height).fill(UNREACHABLE);
  if (source < 0 || source >= cost.length || hard[source] !== 0) return cost;
  cost[source] = soft[source] ?? 0;
  let frontier: number[] = [source];
  for (let level = cost[source] ?? 0; frontier.length > 0 && level < 64; level++) {
    const queue = frontier.filter((c) => cost[c] === level);
    const next: number[] = [];
    for (let i = 0; i < queue.length; i++) {
      const cell = queue[i] ?? 0;
      const x = cell % width;
      const around = [x > 0 ? cell - 1 : -1, x < width - 1 ? cell + 1 : -1, cell - width, cell + width];
      for (const n of around) {
        if (n < 0 || n >= cost.length || hard[n] !== 0) continue;
        const c = level + (soft[n] ?? 0);
        if (c >= (cost[n] ?? 0)) continue;
        cost[n] = c;
        if (c === level) queue.push(n);
        else next.push(n);
      }
    }
    frontier = next;
  }
  return cost;
}

export function probeNav(def: MissionDef): NavProbe {
  const statics = buildStatics(def, def.props.map((p) => ({ id: p.id, destroyed: false, looted: false })));
  const { width, height } = statics.nav;
  const blocked = Uint8Array.from(statics.nav.blocked);
  const size = width * height;

  // The water barrier: impassable except at its crossings.
  for (const b of def.waterBarriers) {
    const zs = b.course.points.map((p) => p[1]);
    const z0 = Math.max(0, Math.floor(Math.min(...zs) - b.course.width / 2 - 1));
    const z1 = Math.min(height - 1, Math.ceil(Math.max(...zs) + b.course.width / 2 + 1));
    for (let z = z0; z <= z1; z++) {
      for (let x = 0; x < width; x++) if (inBarrier(def, x + 0.5, z + 0.5)) blocked[z * width + x] = 1;
    }
  }

  // Wire-only cells: blocked by wire and by nothing else.
  const pad = RANGER.radius + 0.15;
  const soft = new Uint8Array(size);
  const others: Collider[] = [];
  for (const p of def.props) if (p.kind !== 'wire') for (const c of collidersFor(p)) if (c.blocksMove) others.push(c);
  for (const p of def.props) {
    if (p.kind !== 'wire') continue;
    for (const c of collidersFor(p)) {
      const zEnd = Math.min(height - 1, Math.floor(c.z + c.hz + pad));
      const xEnd = Math.min(width - 1, Math.floor(c.x + c.hx + pad));
      for (let z = Math.max(0, Math.floor(c.z - c.hz - pad)); z <= zEnd; z++) {
        for (let x = Math.max(0, Math.floor(c.x - c.hx - pad)); x <= xEnd; x++) {
          const px = x + 0.5;
          const pz = z + 0.5;
          if (!covers(c, px, pz, pad) || inPond(def, px, pz) || inBarrier(def, px, pz)) continue;
          if (!others.some((o) => covers(o, px, pz, pad))) soft[z * width + x] = 1;
        }
      }
    }
  }
  const hard = new Uint8Array(size);
  for (let i = 0; i < size; i++) hard[i] = blocked[i] !== 0 && soft[i] === 0 ? 1 : 0;
  for (const f of def.mineFields) {
    for (let z = Math.max(0, Math.floor(f.rect.z0 - 0.5)); z <= Math.min(height - 1, Math.floor(f.rect.z1)); z++) {
      for (let x = Math.max(0, Math.floor(f.rect.x0 - 0.5)); x <= Math.min(width - 1, Math.floor(f.rect.x1)); x++) {
        const inside = x + 0.5 >= f.rect.x0 && x + 0.5 <= f.rect.x1 && z + 0.5 >= f.rect.z0 && z + 0.5 <= f.rect.z1;
        if (inside && hard[z * width + x] === 0) soft[z * width + x] = 1;
      }
    }
  }

  const costly = new Uint8Array(size);
  const pathCost = statics.nav.cost;
  if (pathCost) for (let i = 0; i < size; i++) costly[i] = (pathCost[i] ?? 1) > 1 ? 1 : 0;

  const full: NavGrid = { width, height, blocked };
  const source = Math.floor(def.pickup.z) * width + Math.floor(def.pickup.x);
  const cost = softCost(width, height, hard, soft, source);
  const component = labelComponents(full);
  return { width, height, full, hard, soft, cost, component, costly, colliders: statics.colliders };
}
